/**
 * Pulse Quantity Breaks
 * Mobile-first quantity tier selector integration with product form
 * Minimal JS with event delegation
 */

(function() {
  'use strict';

  if (!document.querySelector('[data-pulse-qty-breaks]')) {
    return;
  }

  class PulseQuantityBreaks {
    constructor(container) {
      this.container = container;
      this.sectionId = container.dataset.sectionId;
      this.blockId = container.dataset.blockId;
      this.qtyTarget = container.dataset.pulseQtyTarget || '[name="quantity"]';
      this.productForm = this.findProductForm();
      this.qtyInput = this.findQuantityInput();
      this.allowCustomQuantity = container.dataset.allowCustomQuantity !== 'false';
      
      this.init();
    }

    findProductForm() {
      // Find closest product form
      let form = this.container.closest('form[action*="/cart/add"]');
      if (!form) {
        form = document.querySelector(`#product-form-${this.sectionId}`);
      }
      if (!form) {
        form = document.querySelector('form[action*="/cart/add"]');
      }
      return form;
    }

    findQuantityInput() {
      if (!this.productForm) return null;
      
      // Try to find existing quantity input
      let input = this.productForm.querySelector(this.qtyTarget);
      
      // If not found and custom quantity not allowed, create hidden input
      if (!input && !this.allowCustomQuantity) {
        input = document.createElement('input');
        input.type = 'hidden';
        input.name = 'quantity';
        input.value = '1';
        this.productForm.appendChild(input);
      }
      
      return input;
    }

    init() {
      // Handle radio inputs
      const radios = this.container.querySelectorAll('input[type="radio"].pulse-qty-breaks__input');
      radios.forEach(radio => {
        radio.addEventListener('change', this.handleRadioChange.bind(this));
      });

      // Handle pill buttons
      const pills = this.container.querySelectorAll('.pulse-qty-breaks__pill');
      pills.forEach(pill => {
        pill.addEventListener('click', this.handlePillClick.bind(this));
      });

      // Listen for variant changes to recalculate (if needed)
      this.handleVariantChange();

      // Set initial value if default selected
      this.setInitialValue();
    }

    handleRadioChange(e) {
      const qty = parseInt(e.target.value, 10);
      this.updateQuantity(qty);
      this.updatePillStates(e.target);
    }

    handlePillClick(e) {
      const pill = e.currentTarget;
      const qty = parseInt(pill.dataset.qty, 10);
      
      // Toggle selection
      const isSelected = pill.classList.contains('pulse-qty-breaks__pill--selected');
      
      // Deselect all pills
      const allPills = this.container.querySelectorAll('.pulse-qty-breaks__pill');
      allPills.forEach(p => {
        p.classList.remove('pulse-qty-breaks__pill--selected');
        p.setAttribute('aria-pressed', 'false');
      });

      // Select clicked pill
      if (!isSelected) {
        pill.classList.add('pulse-qty-breaks__pill--selected');
        pill.setAttribute('aria-pressed', 'true');
        this.updateQuantity(qty);
      } else if (this.allowCustomQuantity) {
        // Allow deselection if custom quantity is allowed
        this.updateQuantity(1);
      }

      // Update radio buttons if they exist
      const radios = this.container.querySelectorAll('input[type="radio"].pulse-qty-breaks__input');
      radios.forEach(radio => {
        if (parseInt(radio.value, 10) === qty && !isSelected) {
          radio.checked = true;
        } else if (isSelected) {
          radio.checked = false;
        }
      });
    }

    updatePillStates(selectedRadio) {
      const qty = parseInt(selectedRadio.value, 10);
      const pills = this.container.querySelectorAll('.pulse-qty-breaks__pill');
      pills.forEach(pill => {
        const pillQty = parseInt(pill.dataset.qty, 10);
        if (pillQty === qty) {
          pill.classList.add('pulse-qty-breaks__pill--selected');
          pill.setAttribute('aria-pressed', 'true');
        } else {
          pill.classList.remove('pulse-qty-breaks__pill--selected');
          pill.setAttribute('aria-pressed', 'false');
        }
      });
    }

    updateQuantity(qty) {
      if (!this.qtyInput) return;

      // Update input value
      this.qtyInput.value = qty;

      // Trigger input event for any listeners
      const inputEvent = new Event('input', { bubbles: true });
      this.qtyInput.dispatchEvent(inputEvent);

      // Trigger change event for quantity-input custom element
      const changeEvent = new Event('change', { bubbles: true });
      this.qtyInput.dispatchEvent(changeEvent);
    }

    setInitialValue() {
      // Find initially selected tier
      const selectedRadio = this.container.querySelector('input[type="radio"]:checked');
      const selectedPill = this.container.querySelector('.pulse-qty-breaks__pill--selected');
      
      if (selectedRadio) {
        const qty = parseInt(selectedRadio.value, 10);
        this.updateQuantity(qty);
        this.updatePillStates(selectedRadio);
      } else if (selectedPill) {
        const qty = parseInt(selectedPill.dataset.qty, 10);
        this.updateQuantity(qty);
      }
    }

    handleVariantChange() {
      // Listen for variant changes via custom events or DOM mutations
      // This is display-only recalculation (actual price updates handled by theme)
      const productInfo = document.querySelector(`#ProductInfo-${this.sectionId}`);
      if (productInfo) {
        // Listen for variant change events
        productInfo.addEventListener('variant:change', () => {
          // Price display updates are handled by theme's price component
          // This block only controls quantity selection
        });
      }
    }
  }

  // Initialize all quantity breaks blocks
  function initQuantityBreaks() {
    const containers = document.querySelectorAll('[data-pulse-qty-breaks]');
    containers.forEach(container => {
      if (!container.dataset.initialized) {
        container.dataset.initialized = 'true';
        new PulseQuantityBreaks(container);
      }
    });
  }

  // Initialize on DOM ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initQuantityBreaks);
  } else {
    initQuantityBreaks();
  }

  // Re-initialize for dynamic content (e.g., quick add modals)
  document.addEventListener('shopify:section:load', initQuantityBreaks);
})();

